import { buildPnlAxisModel } from 'src/components/star-grid/utils/pnlAxis';
/** Pivot the monthly response without recalculating server-owned statement totals or signs. */
import {
  PNL_CUBE_MONTH_COLUMN,
  PNL_CUBE_SCENARIO_COLUMN,
  PNL_CUBE_VALUE_COLUMN,
  PNL_ROLLUP_NODE_COLUMN
} from 'src/components/star-grid/starGridConstants';
import { PnlGridRow, PnlPivotModel, PnlQueryResponse, PnlScenarioEntry, PnlVariancePair } from 'src/components/star-grid/starGridTypes';
import { toNumber } from 'src/components/star-grid/utils/pnlGrid';
import { additionalPeriodGroups, declaredPeriodGroups, monthlyVarianceColumns, pivotFieldId } from 'src/components/star-grid/utils/pnlPeriods';

// Retain the existing public helper imports used by callers.
export { formatMonthShort, pivotFieldId } from 'src/components/star-grid/utils/pnlPeriods';

export const pnlRowKey = (row: PnlGridRow, dimensions: readonly string[]): string => JSON.stringify(dimensions.map((field) => row[field] ?? null));
const columnNames = (response: PnlQueryResponse, kind: string): string[] => [
  ...new Set(response.columns.filter((column) => column.kind === kind).map((column) => column.name))
];

const collectWideRows = (response: PnlQueryResponse, dimensions: readonly string[]) => {
  const wideRows = new Map<string, PnlGridRow>();
  const periods = new Map<number, Set<number>>();
  const display = columnNames(response, 'display');
  for (const row of response.rows) {
    const scenario = toNumber(row[PNL_CUBE_SCENARIO_COLUMN]);
    const month = toNumber(row[PNL_CUBE_MONTH_COLUMN]);
    if (scenario === null || month === null || !Number.isInteger(scenario) || !Number.isInteger(month)) continue;
    const key = pnlRowKey(row, dimensions);
    const wide = wideRows.get(key) ?? Object.fromEntries(dimensions.map((field) => [field, row[field] ?? null]));
    for (const field of display) if (row[field] !== null && row[field] !== undefined) wide[field] = row[field];
    wide[pivotFieldId(scenario, month)] = toNumber(row[PNL_CUBE_VALUE_COLUMN]);
    wideRows.set(key, wide);
    periods.set(scenario, (periods.get(scenario) ?? new Set<number>()).add(month));
  }
  return { rows: [...wideRows.values()], periods };
};

/** Use only explicit statement row_index; sort_order/sibling_order are not report order. */
export const orderPnlRows = (rows: PnlGridRow[]): PnlGridRow[] =>
  rows.sort((a, b) => {
    const left = toNumber(a.row_index);
    const right = toNumber(b.row_index);
    if (left === null) return right === null ? 0 : 1;
    return right === null ? -1 : left - right;
  });

export const buildPnlPivotModel = (
  response: PnlQueryResponse,
  scenarios: readonly PnlScenarioEntry[],
  pairs: readonly PnlVariancePair[]
): PnlPivotModel => {
  if (response.axis) return buildPnlAxisModel(response.axis, response, scenarios);
  const dimensionFields = columnNames(response, 'dimension').filter((field) => ![PNL_CUBE_SCENARIO_COLUMN, PNL_CUBE_MONTH_COLUMN].includes(field));
  const { rows, periods } = collectWideRows(response, dimensionFields);
  const declared = declaredPeriodGroups(scenarios);
  return {
    // A rollup response groups by the closure ancestor; its rows already hold their own subtotal.
    isRollup: dimensionFields.includes(PNL_ROLLUP_NODE_COLUMN),
    dimensionFields,
    columnGroups: [...declared, ...additionalPeriodGroups(periods, declared)],
    calculatedColumns: monthlyVarianceColumns(pairs, scenarios),
    rows: orderPnlRows(rows)
  };
};
